/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { ComponentStore } from '@ngrx/component-store';
import {
    Observable,
    Subject,
    Subscription,
} from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';

import {
    IApplicationLog,
    IConnectionLog,
    IEventLog,
} from 'generated-types';

import {
    ILogsStates,
    LogsStore,
    TLogListPaginationStates,
} from '../../services/logs.store';
import { LogsType, TLog } from '../../logs.types';

/**
 * State type for the store.
 * There can be either applicationLog, connectionLog or eventLog set as they are mutually exclusive.
 * @param hasPreviousLog - True if there's previous log entry to switch to.
 * @param hasNextLog - True if there's a next log entry to switch to.
 * @param applicationLog - Exist only when the VS is of type L7.
 * @param connectionLog - Exist only when the VS is of type L4 or DNS.
 * @param eventLog - Exist only with event log pages.
 */
export interface ILogCinematicStates {
    applicationLog?: IApplicationLog;
    connectionLog?: IConnectionLog;
    eventLog?: IEventLog;
    hasPreviousLog: boolean;
    hasNextLog: boolean;
}

/**
 * @description
 *     An abstract class for state and Effect management for LogCinematic and its children components.
 * @author Zhiqian Liu
 */
export abstract class LogCinematicStore<T extends ILogCinematicStates> extends ComponentStore<T> {
    public readonly hasPreviousLog$ = this.select<boolean>(({ hasPreviousLog }) => hasPreviousLog);
    public readonly hasNextLog$ = this.select<boolean>(({ hasNextLog }) => hasNextLog);

    // *************************************** Updaters ****************************************

    public readonly setErrors = this.updater((state, errors: string | object) => ({
        ...state,
        errors,
    }));

    public readonly setErrorFlag = this.updater((state, hasError: boolean) => ({
        ...state,
        hasError,
    }));

    /**
     * Update state with the newly switched log, depending on the type of log.
     */
    public readonly setLogBasedOnType = this.effect<TLog>(
        log$ => log$.pipe(
            withLatestFrom(this.logsStore.logsType$),
            tap(([log, logsType]: [TLog, LogsType]) => {
                switch (log && logsType) {
                    case LogsType.APPLICATION:
                        this.setApplicationLog(log as IApplicationLog);
                        break;

                    case LogsType.CONNECTION:
                        this.setConnectionLog(log as IConnectionLog);
                        break;

                    case LogsType.EVENT:
                        this.setEventLog(log as IEventLog);
                        break;
                }
            }),
        ),
    );

    protected readonly setApplicationLog = this.updater((state, applicationLog: IApplicationLog) => ({
        ...state,
        applicationLog,
    }));

    protected readonly setConnectionLog = this.updater((state, connectionLog: IApplicationLog) => ({
        ...state,
        connectionLog,
    }));

    protected readonly setEventLog = this.updater((state, eventLog: IEventLog) => ({
        ...state,
        eventLog,
    }));

    protected readonly setHasPreviousLog = this.updater((state, hasPreviousLog: boolean) => ({
        ...state,
        hasPreviousLog,
    }));

    protected readonly setHasNextLog = this.updater((state, hasNextLog: boolean) => ({
        ...state,
        hasNextLog,
    }));

    // ********************************** Abstract properties *********************************

    /**
     * Select the log loaded and being inspected.
     * Should be overridden by the concrete store.
     * The override should guarantee an empty object is returned if nothing is loaded.
     */
    public abstract log$: Observable<TLog>;

    /**
     * Subject to close the cinematic view.
     * Must be overridden by the concrete store and be completed upon concrete store destruction.
     */
    public abstract closeCinematic$: Subject<void>;

    /**
     * Subscription to logListStore.recentlyViewedLog$ to update the log being inspected.
     * Must be overridden by the concrete store.
     */
    protected abstract logUpdateSubscription: Subscription;

    /**
     * Subscription to logListStore's recentlyViewedLogIndex$ and logStore's logListPaginationInfo$ to update
     * neighbor log flags indicating whether there's previous or next log.
     * Must be overridden by the concrete store.
     */
    protected abstract neighborLogFlagsSubscription: Subscription;

    // ************************************* Constructor *************************************

    constructor(
        protected readonly logsStore: LogsStore<ILogsStates>,
        initialState: T,
    ) {
        super(initialState);
    }

    // ************************************* Methods *************************************

    /**
     * Close the cinematic view.
     */
    public closeCinematic(): void {
        this.closeCinematic$.next();
    }

    /**
     * Set the flags for the previous and next log based on the current log index in the list and the pagination info.
     */
    protected setNeighborLogFlags(currentIndex: number, logListPaginationInfo: TLogListPaginationStates): void {
        const { page, pageSize, listLogCount } = logListPaginationInfo;

        // page starts from 1, currentIndex starts from 0
        const overallPosition = currentIndex + 1 + (page - 1) * pageSize;

        this.setHasPreviousLog(overallPosition > 1);
        this.setHasNextLog(overallPosition < listLogCount);
    }
}
